import { Form, message } from 'antd';
import { useState } from 'react';

const normalizeTagsArray = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) {
    return tags.map((t) => String(t || '').trim()).filter(Boolean);
  }
  const s = String(tags || '').trim();
  if (!s) return [];
  return s.split(/[,，]/).map((t) => t.trim()).filter(Boolean);
};

export const useContentListDetail = ({
  getContentDetail,
  modifyContentDetail,
  setDataSource,
  reloadListAfterUpdate,
}) => {
  const [detailForm] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [contentDetail, setContentDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');

  const closeDetailDrawer = () => {
    setDrawerVisible(false);
    setContentDetail(null);
    detailForm.resetFields();
  };

  const handleContentIdClick = async (contentId, initialTab = 'preview') => {
    setDrawerVisible(true);
    setDetailLoading(true);
    setActiveTab(initialTab);
    try {
      const response = await getContentDetail({ content_id: contentId });
      if (response.status === 'success' && response.data) {
        setContentDetail(response.data);
        const bodyText = response.data.body ?? response.data.content ?? '';
        detailForm.setFieldsValue({
          title: response.data.title,
          summary: response.data.summary,
          body: bodyText,
          tags: normalizeTagsArray(response.data.tags),
          source: response.data.source,
          content_type: response.data.content_type,
          creator_user_id: response.data.creator_user_id,
          risk_flag: response.data.risk_flag,
          status: response.data.status,
          available_platforms: response.data.available_platforms || [],
          manual_review_required: response.data.manual_review_required,
          is_manual_reviewed: response.data.is_manual_reviewed,
          primary_category: response.data.primary_category,
          secondary_category: response.data.secondary_category,
        });
      } else {
        message.error('获取内容详情失败');
        setDrawerVisible(false);
      }
    } catch (error) {
      console.error('获取内容详情失败:', error);
      message.error('获取内容详情失败，请稍后重试');
      setDrawerVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailSave = async () => {
    try {
      const values = await detailForm.validateFields();
      const submitValues = {
        ...values,
        tags: values.tags
          ? (Array.isArray(values.tags)
            ? values.tags.map((t) => String(t || '').trim()).filter(Boolean)
            : String(values.tags).split(/[,，]/).map((t) => t.trim()).filter(Boolean))
          : [],
      };
      const response = await modifyContentDetail({
        content_id: contentDetail.content_id,
        ...submitValues,
      });
      if (response.status === 'success') {
        message.success('保存成功');
        const contentId = contentDetail.content_id;
        setDataSource((prev) => (prev || []).map((item) => {
          if (item?.content_id !== contentId) return item;
          return {
            ...item,
            ...submitValues,
            risk_flag: submitValues.risk_flag,
            primary_category: submitValues.primary_category,
            secondary_category: submitValues.secondary_category,
            category_level1: submitValues.primary_category,
            category_level2: submitValues.secondary_category,
          };
        }));
        closeDetailDrawer();
        await reloadListAfterUpdate();
      } else {
        message.error('保存失败');
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败，请稍后重试');
    }
  };

  const handleDetailReset = () => {
    detailForm.setFieldsValue({
      title: contentDetail.title,
      summary: contentDetail.summary,
      body: contentDetail.body ?? contentDetail.content ?? '',
      tags: normalizeTagsArray(contentDetail.tags),
      source: contentDetail.source,
      content_type: contentDetail.content_type,
      creator_user_id: contentDetail.creator_user_id,
      risk_flag: contentDetail.risk_flag,
      status: contentDetail.status,
      available_platforms: contentDetail.available_platforms || [],
      manual_review_required: contentDetail.manual_review_required,
      is_manual_reviewed: contentDetail.is_manual_reviewed,
      primary_category: contentDetail.primary_category,
      secondary_category: contentDetail.secondary_category,
    });
  };

  return {
    detailForm,
    drawerVisible,
    detailLoading,
    contentDetail,
    activeTab,
    setActiveTab,
    closeDetailDrawer,
    handleContentIdClick,
    handleDetailSave,
    handleDetailReset,
    normalizeTagsArray,
  };
};
